if (!customElements.get('duque-text-personalization')) {
  var COMBINING_MARK_MIN = 768; // U+0300
  var COMBINING_MARK_MAX = 879; // U+036F
  var ALLOWED_CHAR_PATTERN = /^[A-Z0-9 \-']+$/;

  function stripDiacritics(value) {
    var decomposed = value.normalize('NFD');
    var result = '';
    for (var i = 0; i < decomposed.length; i++) {
      var code = decomposed.charCodeAt(i);
      if (code >= COMBINING_MARK_MIN && code <= COMBINING_MARK_MAX) continue;
      result += decomposed[i];
    }
    return result;
  }

  class DuqueTextPersonalization extends HTMLElement {
    constructor() {
      super();
      this.isComposing = false;
      this.warningTimeout = null;

      this.onInput = this.onInput.bind(this);
      this.onPaste = this.onPaste.bind(this);
      this.onCompositionStart = this.onCompositionStart.bind(this);
      this.onCompositionEnd = this.onCompositionEnd.bind(this);
      this.onBlur = this.onBlur.bind(this);
    }

    connectedCallback() {
      this.input = this.querySelector('.duque-text-personalization__input');
      this.countElement = this.querySelector('[data-duque-text-personalization-count]');
      this.warningElement = this.querySelector('[data-duque-text-personalization-warning]');

      if (!this.input) return;

      this.maxLength = parseInt(this.dataset.maxLength, 10) || 10;
      this.verbSingular = this.dataset.countVerbSingular || '';
      this.verbPlural = this.dataset.countVerbPlural || '';
      this.nounSingular = this.dataset.countNounSingular || '';
      this.nounPlural = this.dataset.countNounPlural || '';

      this.input.addEventListener('input', this.onInput);
      this.input.addEventListener('paste', this.onPaste);
      this.input.addEventListener('compositionstart', this.onCompositionStart);
      this.input.addEventListener('compositionend', this.onCompositionEnd);
      this.input.addEventListener('blur', this.onBlur);

      this.updateCount(this.input.value.length);
    }

    disconnectedCallback() {
      if (!this.input) return;

      this.input.removeEventListener('input', this.onInput);
      this.input.removeEventListener('paste', this.onPaste);
      this.input.removeEventListener('compositionstart', this.onCompositionStart);
      this.input.removeEventListener('compositionend', this.onCompositionEnd);
      this.input.removeEventListener('blur', this.onBlur);

      if (this.warningTimeout) {
        window.clearTimeout(this.warningTimeout);
      }
    }

    onCompositionStart() {
      this.isComposing = true;
    }

    onCompositionEnd() {
      this.isComposing = false;
      this.sanitize();
    }

    onInput() {
      // While an IME composition is in progress (accented input methods,
      // predictive keyboards on some Android devices), the browser fires
      // `input` events for intermediate, not-yet-final characters. Sanitizing
      // those breaks the composition, so we wait for `compositionend`.
      if (this.isComposing) return;
      this.sanitize();
    }

    onPaste() {
      // The pasted text isn't in `input.value` yet when `paste` fires.
      window.setTimeout(() => this.sanitize(), 0);
    }

    onBlur() {
      this.sanitize();
    }

    sanitize() {
      var input = this.input;
      var original = input.value;
      var selectionStart = input.selectionStart === null ? original.length : input.selectionStart;

      var cleaned = '';
      var cursorOffset = 0;
      var originalIndex = 0;
      var droppedInvalid = false;

      for (const char of original) {
        var transformed = stripDiacritics(char).replace(/ñ/g, 'n').replace(/Ñ/g, 'N').toUpperCase();

        var kept = '';
        if (transformed !== '' && ALLOWED_CHAR_PATTERN.test(transformed)) {
          kept = transformed;
        } else if (transformed !== '') {
          droppedInvalid = true;
        }

        if (originalIndex < selectionStart) {
          cursorOffset += kept.length;
        }

        cleaned += kept;
        originalIndex += char.length;
      }

      var truncated = cleaned.slice(0, this.maxLength);

      if (truncated !== original) {
        var newCursor = Math.min(cursorOffset, truncated.length);
        input.value = truncated;
        input.setSelectionRange(newCursor, newCursor);
      }

      this.updateCount(truncated.length);
      this.toggleWarning(droppedInvalid);
    }

    updateCount(length) {
      if (!this.countElement) return;
      var remaining = Math.max(this.maxLength - length, 0);
      var isSingular = remaining === 1;
      var verb = isSingular ? this.verbSingular : this.verbPlural;
      var noun = isSingular ? this.nounSingular : this.nounPlural;
      var parts = [verb, String(remaining), noun].filter(function (part) {
        return part !== '';
      });
      this.countElement.textContent = parts.join(' ');
    }

    toggleWarning(show) {
      if (!this.warningElement) return;

      if (this.warningTimeout) {
        window.clearTimeout(this.warningTimeout);
        this.warningTimeout = null;
      }

      if (!show) {
        this.warningElement.hidden = true;
        return;
      }

      this.warningElement.hidden = false;
      this.warningTimeout = window.setTimeout(() => {
        this.warningElement.hidden = true;
      }, 3000);
    }
  }

  customElements.define('duque-text-personalization', DuqueTextPersonalization);
}
